import {getUpRefUid} from '../helpers/upRefCookieHelpers';
import migrationContext from './migrationContext';
import posthogService from './posthogService';
import abTestService from './abTestService';

/**
 * @typedef {Object} PendingPosthogEvent
 * @property {string} eventName
 * @property {Object} properties
 * @property {Date} timestamp
 */

class TrackingService {
	constructor() {
		/** @type {PendingPosthogEvent[]} */
		this.pendingPosthogEvents = [];
		this.flushOnPageHide = () => this._flushPendingPosthogEvents(true);
	}

	/**
	 * Dispatch a single analytics event to both GTM (dataLayer) and PostHog with
	 * an explicit event name. GA-style props (eventCategory/eventAction/eventLabel)
	 * are forwarded to both during the GTM -> PostHog transition.
	 *
	 * Every call is stamped with the migration envelope (distinct_id, experiment_arm,
	 * landing_domain, source, session_id) so cross-system attribution survives the
	 * et-v1 → et-v2 migration.
	 *
	 * Pass `{gtm: false}` when a legacy `dataLayer.push()` for this same event
	 * already exists at the call site (e.g. the GA4 ecommerce view_item / add_to_cart
	 * payloads), so GTM is not double-fed — PostHog still receives the canonical,
	 * envelope-stamped event.
	 * @param {string} eventName
	 * @param {!Object} [properties]
	 * @param {{gtm?: boolean}} [options]
	 * @returns {void}
	 */
	track(eventName, properties = {}, {gtm = true} = {}) {
		if (!eventName) {
			return;
		}

		const envelope = migrationContext.resolve(),
		      enriched = {...envelope, ...properties};

		if (gtm) {
			// GTM receives the event (consent is handled by GTM's own Cookiebot layer).
			window.dataLayer ||= [];
			window.dataLayer.push({
				'event': eventName,
				'uid'  : getUpRefUid(),
				...enriched
			});
		}

		if (!posthogService.isEnabled()) {
			return;
		}

		if (abTestService.areAssignmentsResolved) {
			posthogService.track(eventName, enriched);

			return;
		}

		// Preserve occurrence time while awaiting assignment. Once ready, capture
		// synchronously so navigation after add_to_cart cannot overtake tracking.
		this.pendingPosthogEvents.push({eventName, 'properties': enriched, 'timestamp': new Date()});

		if (this.pendingPosthogEvents.length === 1) {
			window.addEventListener('pagehide', this.flushOnPageHide, {'once': true});
			abTestService.whenAssignmentsReady()
				.then(() => this._flushPendingPosthogEvents(false))
				.catch((err) => {
					this._flushPendingPosthogEvents(false);
					posthogService.captureError(err);
				});
		}
	}

	/**
	 * @param {boolean} flushOnPageHide
	 * @private
	 */
	_flushPendingPosthogEvents(flushOnPageHide) {
		window.removeEventListener('pagehide', this.flushOnPageHide);

		const pending = this.pendingPosthogEvents;

		this.pendingPosthogEvents = [];
		pending.forEach(({eventName, properties, timestamp}) => {
			posthogService.track(eventName, properties, {timestamp, flushOnPageHide});
		});
	}
}

export default new TrackingService();